import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useStockSettings } from "./useStockSettings";

export interface ProductMargin {
  id: string;
  name: string;
  price: number;
  cost: number;
  marginValue: number;
  marginPercent: number;
  hasRecipe: boolean;
  categoryName: string | null;
}

export function useProductMargins() {
  const { activeRestaurant } = useRestaurantContext();
  const { lowMarginAlertPercentage } = useStockSettings();

  const { data: products, isLoading } = useQuery({
    queryKey: ["product-margins", activeRestaurant?.id],
    queryFn: async () => {
      if (!activeRestaurant) return [];

      // Fetch products with categories
      const { data: productsData, error: productsError } = await supabase
        .from("products")
        .select(`
          id,
          name,
          price,
          category:categories(name)
        `)
        .eq("restaurant_id", activeRestaurant.id)
        .eq("is_active", true)
        .order("name");

      if (productsError) throw productsError;

      // Fetch all product ingredients with costs
      const { data: ingredientsData, error: ingredientsError } = await supabase
        .from("product_ingredients")
        .select(`
          product_id,
          quantity,
          ingredient:ingredients(cost_per_unit)
        `);

      if (ingredientsError) throw ingredientsError;

      // Group ingredients by product_id and calculate costs
      const costsByProduct: Record<string, { cost: number; hasRecipe: boolean }> = {};
      
      for (const item of ingredientsData || []) {
        const productId = item.product_id;
        const unitCost = (item.ingredient as any)?.cost_per_unit || 0;
        const itemCost = item.quantity * unitCost;

        if (!costsByProduct[productId]) {
          costsByProduct[productId] = { cost: 0, hasRecipe: false };
        }
        costsByProduct[productId].cost += itemCost;
        costsByProduct[productId].hasRecipe = true;
      }

      // Build the result
      const result: ProductMargin[] = (productsData || []).map((product) => {
        const price = Number(product.price);
        const productCost = costsByProduct[product.id] || { cost: 0, hasRecipe: false };
        const cost = productCost.cost;
        const marginValue = price - cost;
        const marginPercent = price > 0 ? (marginValue / price) * 100 : 0;

        return {
          id: product.id,
          name: product.name,
          price,
          cost,
          marginValue,
          marginPercent,
          hasRecipe: productCost.hasRecipe,
          categoryName: (product.category as any)?.name || null,
        };
      });

      return result;
    },
    enabled: !!activeRestaurant,
  });

  const lowMarginProducts = products?.filter(
    (p) => p.hasRecipe && p.marginPercent < lowMarginAlertPercentage
  ) || [];

  const healthyMarginProducts = products?.filter(
    (p) => !p.hasRecipe || p.marginPercent >= lowMarginAlertPercentage
  ) || [];

  return {
    products: products || [],
    lowMarginProducts,
    healthyMarginProducts,
    isLoading,
    lowMarginAlertPercentage,
  };
}
